import imagemIniciativa from 'assets/img/2026/imagem_iniciativa.png';
import { IniciativaFormatada } from "types/iniciativa";
import { getElbbCorCategoria } from 'utils/elbbPerspective';
import { clearRegistrationUser, getSharePointVideoThumbnail } from 'utils/fns';
import { api } from "./api";
import { dedupedFetch } from "./cache";

/** Extrai matrícula numérica (remove F e zeros à esquerda) */
const extractRegistration = (id: string): string => {
  if (!id) return '';
  return id.replace(/^[Ff]/, '').replace(/^0+/, '');
};

/** Normaliza prefixo (ex: "F0009908" → "9908") */
const normalizePrefix = (prefixo?: string | number): string => {
  if (!prefixo && prefixo !== 0) return '';
  const value = String(prefixo).trim();
  return value.replace(/^[Ff]?0*/, '') || value;
};

/** Retorna nomeGuerra ou nome completo */
const getPreferredName = (source?: any): string => {
  return source?.extra?.nome_guerra || source?.nome_guerra || source?.nome || source?.name || source?.extra?.nome || '';
};

/** Formata nome para exibição: "NOME (F1234567 - 9908)" */
const formatDisplayName = (nome: string, matricula: string, prefixo?: string): string => {
  const reg = extractRegistration(matricula);
  const pref = normalizePrefix(prefixo);
  const suffix = pref ? `F${reg} - ${pref}` : `F${reg}`;
  return `${nome} (${suffix})`;
};

interface BuscarIniciativasParams {
  start?: string;
  status?: string;
  userId?: string;
}

const getCategoryPremioCanonical = (iniciativa: any): string => {
  return (iniciativa.categoryPremio || iniciativa.CategoryPremio || '').toUpperCase();
};

const getPerspectivaOficial = (perspectiva?: string): string => {
  const perspectivaNormalizada = (perspectiva || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

  const perspectivaMap: Record<string, string> = {
    'asg': 'ASG',
    'pessoas e cultura': 'Pessoas e Cultura',
    'transformacao digital e processos': 'Transformação Digital e Processos',
    'financeira': 'Financeira',
    'clientes': 'Clientes'
  };

  return perspectivaMap[perspectivaNormalizada] || perspectiva || '';
};

const getCategoriaInfo = (iniciativa: any): { categoria: string; corCategoria: string } => {
  const categoryPremioCanonical = getCategoryPremioCanonical(iniciativa);

  // Verifica primeiro se é ELBB explicitamente (com c minúsculo)
  if (categoryPremioCanonical === 'ELBB') {
    const perspectivaElbb = iniciativa.indicator?.perspectiva || iniciativa.perspectiva || iniciativa.tema;
    return {
      categoria: 'Prêmio ELBB',
      corCategoria: getElbbCorCategoria(perspectivaElbb) || 'green-bb'
    };
  }

  if (categoryPremioCanonical === 'CONEXAO') {
    return { categoria: 'Prêmio Conexão', corCategoria: 'blue-bb-6' };
  }

  // Se não for ELBB, verifica pela diretoria
  const diretoria = iniciativa.diretoria || '';
  const categoriaMap: Record<string, { categoria: string; corCategoria: string }> = {
    'TRANSFORMAÇÃO DIGITAL': { categoria: 'Joga Junto', corCategoria: 'blue-bb-6' },
    'PROCESSOS': { categoria: 'Joga Junto', corCategoria: 'blue-bb-6' },
  };

  for (const [key, value] of Object.entries(categoriaMap)) {
    if (diretoria.toUpperCase().includes(key)) {
      return value;
    }
  }

  // Se não for ELBB nem Joga Junto, é Prêmio Conceito
  return { categoria: 'Prêmio Conceito', corCategoria: 'pink-bb' };
};

const formatarIniciativa = (iniciativa: any, userId?: string): IniciativaFormatada => {
  const { categoria, corCategoria } = getCategoriaInfo(iniciativa);
  const categoryPremio = getCategoryPremioCanonical(iniciativa);
  const perspectiva = getPerspectivaOficial(iniciativa.indicator?.perspectiva);
  const tema = perspectiva || iniciativa.title || 'Tema não informado';
  const categoriaExibicao = categoryPremio === 'ELBB'
    ? (perspectiva || tema)
    : categoria;

  const titulo = iniciativa.title ||
                 iniciativa.diretoria ||
                 iniciativa.indicator?.perspectiva ||
                 'Iniciativa';

  const descricao = iniciativa.description ||
                    iniciativa.result ||
                    `Iniciativa de ${iniciativa.owner_name_guerra || iniciativa.owner_name || 'Equipe'}`;

  const resultado = String(iniciativa.result || '');

  const autores = iniciativa.owner_name_guerra ||
                  iniciativa.owner_name ||
                  iniciativa.team?.map((t: any) => t.name).join(', ') ||
                  'Equipe';

  const pergunta = iniciativa.indicator?.nome
    ? `Por que essa iniciativa foi fundamental para o atingimento do direcionador estratégico ${iniciativa.indicator.nome}?`
    : 'Por que essa iniciativa foi fundamental para o atingimento do direcionador estratégico?';

  const diretoria = iniciativa.diretoria ||
                    iniciativa.owner_nome_dependencia ||
                    categoria;

  // Formatar data de cadastro/inscrição
  const formatarData = (data: string | number | undefined) => {
    if (!data) return '';
    const date = new Date(data as any);
    if (isNaN(date.getTime())) return '';
    const dia = String(date.getDate()).padStart(2, '0');
    const mes = String(date.getMonth() + 1).padStart(2, '0');
    const ano = date.getFullYear();
    return `${dia}/${mes}/${ano}`;
  };

  // Participantes do time (se existir)
  const confirmedPlayers: string[] = iniciativa.confirmedPlayers || [];

  // Normaliza IDs para comparação (remove prefixo F e zeros à esquerda)
  const isConfirmed = (id: string) =>
    confirmedPlayers.some(cp => clearRegistrationUser(cp) === clearRegistrationUser(id));

  // Monta mapa de target para enriquecer participantes
  const targetById = new Map<string, any>();
  (iniciativa.target || []).forEach((targetMember: any) => {
    const targetId = extractRegistration(targetMember?._id || targetMember?.extra?.id);
    if (targetId) {
      targetById.set(targetId, targetMember);
    }
  });

  const ownerMatricula = extractRegistration(iniciativa.owner);
  const ownerTarget = targetById.get(ownerMatricula);
  const ownerName =
    getPreferredName(ownerTarget) ||
    iniciativa.owner_name_guerra ||
    iniciativa.owner_name ||
    'Responsável';
  const ownerPrefixo = normalizePrefix(
    ownerTarget?.extra?.prefixo || iniciativa.prefixo || iniciativa.original_manager_detail?.prefixo
  );
  const ownerDisplay = formatDisplayName(ownerName, ownerMatricula, ownerPrefixo);

  const participantes: Array<{ nome: string; matricula: string; prefixo?: string; displayName?: string; confirmado: boolean; isOwner?: boolean }> =
    (iniciativa.team || []).map((membro: any) => {
      const matricula = extractRegistration(membro?._id || membro?.extra?.id);
      const targetMember = targetById.get(matricula);
      const nome = getPreferredName(targetMember) || getPreferredName(membro) || 'Participante';
      const prefixo = normalizePrefix(targetMember?.extra?.prefixo || membro?.extra?.prefixo);
      const ownerMatch = ownerMatricula && clearRegistrationUser(matricula) === clearRegistrationUser(ownerMatricula);
      return {
        nome,
        matricula,
        prefixo,
        displayName: formatDisplayName(nome, matricula, prefixo),
        confirmado: isConfirmed(matricula),
        isOwner: !!ownerMatch,
      };
    });

  // Adiciona o owner se não estiver no time
  const ownerJaNoTime = participantes.some(
    p => clearRegistrationUser(p.matricula) === clearRegistrationUser(ownerMatricula || '')
  );
  if (!ownerJaNoTime && ownerMatricula) {
    participantes.unshift({
      nome: ownerName,
      matricula: ownerMatricula,
      prefixo: ownerPrefixo,
      displayName: ownerDisplay,
      confirmado: isConfirmed(ownerMatricula),
      isOwner: true,
    });
  }

  // Processar estatísticas do campo log
  const log = iniciativa.log || [];
  const elogios = log.filter((l: any) => l.actionId === 'elogiar');
  const compartilhamentos = log.filter((l: any) => l.actionId === 'compartilhar');
  const favoritos = log.filter((l: any) => l.actionId === 'favoritar');

  const getNormalizedLogUserId = (logItem: any): string => {
    const rawUserId = logItem?.userId || logItem?.player || logItem?.rawPlayer || '';
    return clearRegistrationUser(String(rawUserId));
  };

  const getLogTime = (logItem: any): number => {
    const time = Number(logItem?.time || logItem?.createdAt || logItem?.created || 0);
    return Number.isFinite(time) ? time : 0;
  };

  const isFromCurrentIniciativa = (logItem: any): boolean => {
    const idIniciativa = logItem?.attributes?.id_iniciativa;
    if (!idIniciativa) return true;
    return String(idIniciativa) === String(iniciativa._id);
  };

  const elogiosDaIniciativa = elogios.filter(isFromCurrentIniciativa);

  const ultimoElogioPorUsuario = new Map<string, any>();
  elogiosDaIniciativa.forEach((l: any) => {
    const normalizedUserId = getNormalizedLogUserId(l);
    if (!normalizedUserId) return;

    const existing = ultimoElogioPorUsuario.get(normalizedUserId);
    if (!existing || getLogTime(l) >= getLogTime(existing)) {
      ultimoElogioPorUsuario.set(normalizedUserId, l);
    }
  });

  // Contar elogios por tipo
  const elogiosMap = new Map<string, number>();
  Array.from(ultimoElogioPorUsuario.values()).forEach((l: any) => {
    const tipoElogio = l.attributes?.elogio;
    if (tipoElogio) {
      elogiosMap.set(tipoElogio, (elogiosMap.get(tipoElogio) || 0) + 1);
    }
  });

  const elogiosPorTipo = Array.from(elogiosMap.entries()).map(([elogio, quantidade]) => ({
    elogio,
    quantidade
  }));

  // Contar favoritos ativos (filtrar pelo último registro de cada usuário com ativo=true)
  const favoritosPorUsuario = new Map<string, { time: number; ativo: boolean }>();
  favoritos.forEach((l: any) => {
    const userId = l.userId;
    const time = l.time || 0;
    const ativo = l.attributes?.ativo !== false; // Default true se não existir

    if (!favoritosPorUsuario.has(userId) || time > (favoritosPorUsuario.get(userId)?.time || 0)) {
      favoritosPorUsuario.set(userId, { time, ativo });
    }
  });

  // Contar apenas usuários com ativo = true (favoritado)
  const totalFavoritos = Array.from(favoritosPorUsuario.values()).filter(f => f.ativo === true).length;

  // Verificar ações do usuário atual
  let usuarioElogio: string | undefined;
  let usuarioFavoritou = false;
  let usuarioCompartilhou = false;

  if (userId) {
    // Buscar último elogio do usuário (normalizado)
    const currentUserId = clearRegistrationUser(userId);
    const ultimoElogioUsuario = ultimoElogioPorUsuario.get(currentUserId);
    usuarioElogio = ultimoElogioUsuario?.attributes?.elogio;

    // Verificar se usuário favoritou (último registro com ativo=true)
    const favoritosUsuario = favoritos.filter((l: any) => l.userId === userId);
    if (favoritosUsuario.length > 0) {
      const ultimoFavorito = favoritosUsuario.sort((a: any, b: any) => (b.time || 0) - (a.time || 0))[0];
      usuarioFavoritou = ultimoFavorito.attributes?.ativo !== false; // Default true se não existir
    }

    // Verificar se usuário compartilhou
    usuarioCompartilhou = compartilhamentos.some((l: any) => l.userId === userId);
  }

  const videoDoc = iniciativa.documento?.find((d: any) => d.type === 'video');

  return {
    iniciativaOriginal: iniciativa,
    id: iniciativa._id,
    titulo,
    tema,
    descricao: descricao.length > 150 ? descricao.substring(0, 150) + '...' : descricao,
    resultado,
    pergunta,
    texto: iniciativa.result || '',
    autores,
    categoria: categoriaExibicao,
    perspectiva,
    categoryPremio,
    corCategoria,
    imagem: getSharePointVideoThumbnail(videoDoc?.file) || iniciativa.owner_avatar?.image?.small?.url || imagemIniciativa,
    status: iniciativa.status,
    diretoria,
    cadastradoPor: iniciativa.owner_name || 'Não informado',
    cadastradoMatricula: iniciativa.owner || '',
    cadastradoData: formatarData(iniciativa.created || iniciativa.createdAt),
    inscritoPor: iniciativa.status === 'inscrita'
      ? (iniciativa.inscricao_player || '--')
      : '--',
    inscritoMatricula: '',
    inscritoData: iniciativa.status === 'inscrita' && iniciativa.inscricao_player
      ? formatarData(iniciativa.dt_inscricao || iniciativa.updatedAt)
      : '',
    participantes,
    documentacao: iniciativa.documento?.find((d: any) => d.type === 'file' && d.file)?.file,
    videoUrl: (() => {
      if (!videoDoc?.file) return undefined;
      return videoDoc.embedUrl || videoDoc.file;
    })(),
    originalVideoUrl: videoDoc?.file,
    direcionadorEstrategico: iniciativa.indicator?.perspectiva || iniciativa.CategoryPremio || diretoria,
    totalElogios: ultimoElogioPorUsuario.size,
    totalCompartilhamentos: compartilhamentos.length,
    totalFavoritos,
    elogiosPorTipo,
    usuarioElogio,
    usuarioFavoritou,
    usuarioCompartilhou,
    classificadaEtapa1: iniciativa.classificada_etapa_1 === true,
    classificadaEtapa2: iniciativa.classificadaEtapa2 === true,
    position: typeof iniciativa.position === 'number' ? iniciativa.position : undefined,
  };
};

export const buscarIniciativas = async (params?: BuscarIniciativasParams): Promise<IniciativaFormatada[]> => {
  try {
    // O endpoint /find/tasks_etapa1 é um find configurado no Funifier Studio
    // que retorna diretamente as iniciativas da collection bb_task__c
    const body: Record<string, any> = { categoria: 'ELBB' };
    if (params?.userId) body.player = params.userId;
    const { data } = await api.post("/find/tasks_etapa1", body);

    // O find do Funifier Studio retorna um array diretamente
    if (!data || !Array.isArray(data)) {
      return [];
    }

    // Mapeia cada iniciativa para o formato utilizado no frontend
    return data.map((ini: any) => formatarIniciativa(ini, params?.userId));
  } catch (error: any) {
    throw error;
  }
};

export const buscarIniciativasPorPlayer = async (userId: string): Promise<IniciativaFormatada[]> => {
  try {
    const { data } = await api.post("/find/tasks_etapa1", {
      player: userId,
      categoria: 'ELBB',
    });

    if (!data || !Array.isArray(data)) {
      return [];
    }

    return data.map((ini: any) => formatarIniciativa(ini, userId));
  } catch (error: any) {
    throw error;
  }
};

export const buscarIniciativaPorId = async (id: string, userId?: string): Promise<IniciativaFormatada | null> => {
  try {
    // Busca usando o find configurado no Funifier Studio
    // passando o _id como filtro para buscar uma iniciativa específica
    const { data } = await api.post("/find/listagem_entregas", {
      _id: id
    });

    // O find retorna um array, então pegamos o primeiro elemento
    if (!data || !Array.isArray(data) || data.length === 0) {
      return null;
    }

    return formatarIniciativa(data[0], userId);
  } catch (error: any) {
    throw error;
  }
};

/**
 * Busca as iniciativas favoritadas pelo usuário
 * Filtra o log de ações do usuário para encontrar favoritos ativos (value = 1)
 */
export const buscarIniciativasFavoritas = async (userId: string): Promise<IniciativaFormatada[]> => {
  try {
    // Busca todas as iniciativas
    const todasIniciativas = await buscarIniciativas({ userId });

    // Filtra apenas as que o usuário favoritou
    return todasIniciativas.filter(iniciativa => iniciativa.usuarioFavoritou);
  } catch (error: any) {
    throw error;
  }
};

export const buscarIniciativasEtapa3 = async (
  userId?: string
): Promise<IniciativaFormatada[]> =>
  dedupedFetch(
    `iniciativas-etapa3-${userId ?? 'anon'}`,
    async () => {
      const { data } = await api.post('/find/tasks_etapa3', {});
      if (!data || !Array.isArray(data)) return [];
      return data
        .filter((ini: any) => (ini.categoryPremio || '').toUpperCase() === 'ELBB')
        .map((ini: any) => formatarIniciativa(ini, userId));
    },
    60_000
  );

export const buscarIniciativasVencedoras = async (
  userId?: string
): Promise<IniciativaFormatada[]> =>
  dedupedFetch(
    `iniciativas-vencedoras-${userId ?? 'anon'}`,
    async () => {
      const { data } = await api.post('/find/resultado_final', {});
      if (!data || !Array.isArray(data)) return [];
      return data
        .filter((ini: any) => (ini.categoryPremio || '').toUpperCase() === 'ELBB')
        .map((ini: any) => formatarIniciativa(ini, userId));
    },
    60_000
  );

export const buscarIniciativasEtapa2 = async (
  category: string,
  userId?: string
): Promise<IniciativaFormatada[]> => {
  return dedupedFetch(
    `iniciativas-etapa2-${category}`,
    async () => {
      try {
        const body: Record<string, any> = { category };
        const { data } = await api.post('/find/tasks_etapa2', body,       {
        headers: { Range: `items=0-500` },
      });

        if (!data || !Array.isArray(data)) {
          return [];
        }

        return data.map((ini: any) => formatarIniciativa(ini, userId));
      } catch (error) {
        console.error('Erro ao buscar iniciativas etapa 2:', error);
        throw error;
      }
    },
    60_000
  );
};
